<script lang="ts">
  import { loadExternalImage, renderMarkdownDocument } from '../logic/markdown';
  import { encodePath, formatBytes, imageMime, imageSource, PREVIEW_MAX, type ImageSource } from '../logic/preview';
  import { rawBytes } from '../logic/rawbytes';
  import { watchTableFrames } from '../logic/tableScroll';

  interface Props {
    /** The document's text; null when the file has no version on this side. */
    text: string | null;
    path: string;
    /** Commit the text is at: relative links open the file there on GitHub, relative images load from it. */
    commit: string | null;
    owner: string;
    repo: string;
    webUrl: string;
    /** Changed source lines: the rendered sections they touch are marked. */
    changed?: Set<number> | null;
    /** Side name shown above the document in a side-by-side view. */
    label?: string | null;
    empty?: string;
  }
  let { text, path, commit, owner, repo, webUrl, changed = null, label = null, empty = 'No file on this side' }: Props = $props();

  /** Repository images loaded per document at most. */
  const IMAGE_LIMIT = 40;

  let force = $state(false);
  let root: HTMLDivElement | undefined = $state();
  const large = $derived(text !== null && text.length > PREVIEW_MAX && !force);
  const doc = $derived(
    text === null || large
      ? null
      : renderMarkdownDocument(text, {
          path,
          changed,
          href: (t) => `${webUrl}/${owner}/${repo}/blob/${encodeURIComponent(commit ?? 'HEAD')}/${encodePath(t.path)}${t.hash}`,
        }),
  );

  $effect(() => {
    void doc;
    if (root) return watchTableFrames(root);
  });

  // Repository-relative images: loaded through the bridge at this commit, shown from `imageSource`.
  $effect(() => {
    const el = root;
    void doc;
    const ref = commit;
    if (!el || !ref) return;
    const sources: ImageSource[] = [];
    let cancelled = false;
    const imgs = [...el.querySelectorAll<HTMLImageElement>('img[data-repo-path]')].slice(0, IMAGE_LIMIT);
    for (const img of imgs) {
      const p = img.dataset.repoPath!;
      const mime = imageMime(p);
      if (!mime) {
        img.classList.add('md-img-missing');
        continue;
      }
      img.classList.add('md-img-loading');
      rawBytes(owner, repo, { path: p, ref }).then(
        (bytes) => {
          if (cancelled) return;
          img.classList.remove('md-img-loading');
          if (!bytes) {
            img.classList.add('md-img-missing');
            img.title = `${p} is not in the repository at ${ref.slice(0, 7)}`;
            return;
          }
          const src = imageSource(bytes, mime);
          sources.push(src);
          img.src = src.url;
        },
        () => {
          if (cancelled) return;
          img.classList.remove('md-img-loading');
          img.classList.add('md-img-missing');
        },
      );
    }
    return () => {
      cancelled = true;
      for (const src of sources) src.release();
    };
  });
</script>

<div class="md-side">
  {#if label}<div class="md-side-label">{label}</div>{/if}
  {#if text === null}
    <div class="preview-message">{empty}</div>
  {:else if large}
    <div class="preview-message">
      This document is {formatBytes(text.length)}, over the {formatBytes(PREVIEW_MAX)} rendered by default.
      <button class="micro" onclick={() => (force = true)}>Render anyway</button>
    </div>
  {:else if doc && !doc.sections}
    <div class="preview-message">Empty document.</div>
  {:else if doc}
    <!-- svelte-ignore a11y_click_events_have_key_events, a11y_no_static_element_interactions (delegates to the "Load image" buttons inside) -->
    <div class="markdown-doc" bind:this={root} onclick={loadExternalImage}>{@html doc.html}</div>
  {/if}
</div>
